<template>
  <div>
    <PageHeader :title="isEdit ? 'Edit Survey' : 'Create Survey'" showBack />

    <!-- Branded preview: show respondents your school branding -->
    <div v-if="schoolLogo" class="survey-brand-preview mb-3">
      <img :src="schoolLogo" :alt="schoolName" class="survey-brand-preview-logo" />
      <div class="ml-3">
        <div class="survey-brand-preview-title">{{ form.title || 'Your Survey' }}</div>
        <small class="text-muted">Respondents will see this branding on your survey page.</small>
      </div>
    </div>

    <!-- Survey Settings -->
    <b-card class="mb-3">
      <b-form @submit.prevent="handleSave">
        <b-row>
          <b-col md="8">
            <b-form-group label="Title">
              <b-form-input v-model="form.title" required placeholder="e.g. Student Satisfaction Survey" />
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Status">
              <b-form-select v-model="form.status" :options="statusOptions" />
            </b-form-group>
          </b-col>
        </b-row>
        <b-form-group label="Description">
          <b-form-textarea v-model="form.description" rows="2" placeholder="Brief description shown to respondents..." />
        </b-form-group>
        <b-row>
          <b-col md="4"><b-form-checkbox v-model="form.collect_email">Collect Email</b-form-checkbox></b-col>
          <b-col md="4"><b-form-checkbox v-model="form.collect_name">Collect Name</b-form-checkbox></b-col>
          <b-col md="4"><b-form-checkbox v-model="form.collect_phone">Collect Phone</b-form-checkbox></b-col>
        </b-row>
        <b-form-group label="Welcome Message" class="mt-3">
          <b-form-textarea v-model="form.welcome_message" rows="2" placeholder="Optional welcome screen message..." />
        </b-form-group>
        <b-form-group label="Thank You Message">
          <b-form-textarea v-model="form.thank_you_message" rows="2" placeholder="Message shown after submission..." />
        </b-form-group>

        <hr />

        <!-- Questions -->
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h5 class="mb-0">Questions</h5>
          <b-button size="sm" variant="outline-primary" @click="addQuestion">+ Add Question</b-button>
        </div>

        <div v-if="form.questions.length === 0" class="text-center text-muted py-4 border rounded mb-3">
          No questions yet. Click "+ Add Question" to start building your survey.
        </div>

        <draggable v-model="form.questions" handle=".drag-handle" @end="reorderQuestions">
          <b-card v-for="(q, index) in form.questions" :key="index" class="mb-2">
            <div class="d-flex align-items-start">
              <div class="drag-handle mr-2" style="cursor: grab; padding-top: 6px;">
                <i class="material-icons text-muted">drag_indicator</i>
              </div>
              <div class="flex-grow-1">
                <b-row>
                  <b-col md="7">
                    <b-form-group label="Question" class="mb-2">
                      <b-form-input v-model="q.title" required placeholder="Enter question text..." />
                    </b-form-group>
                  </b-col>
                  <b-col md="3">
                    <b-form-group label="Type" class="mb-2">
                      <b-form-select v-model="q.type" :options="questionTypeOptions" @change="onTypeChange(q)" />
                    </b-form-group>
                  </b-col>
                  <b-col md="2" class="d-flex align-items-end mb-2">
                    <b-form-checkbox v-model="q.is_required" class="mt-2">Required</b-form-checkbox>
                  </b-col>
                </b-row>

                <!-- Placeholder (text types) -->
                <b-form-group v-if="q.type === 'short_text' || q.type === 'long_text'" label="Placeholder" class="mb-2">
                  <b-form-input v-model="q.placeholder" placeholder="Optional placeholder..." />
                </b-form-group>

                <!-- Options (single/multi select) -->
                <template v-if="q.type === 'single_select' || q.type === 'multi_select'">
                  <label class="mb-1">Options</label>
                  <div v-for="(opt, oi) in (q.extra.options || [])" :key="oi" class="d-flex align-items-center mb-1">
                    <b-form-input v-model="q.extra.options[oi]" size="sm" placeholder="Option text..." class="mr-2" />
                    <b-button size="sm" variant="outline-danger" @click="removeOption(q, oi)">
                      <i class="material-icons" style="font-size: 14px;">close</i>
                    </b-button>
                  </div>
                  <b-button size="sm" variant="outline-secondary" class="mt-1" @click="addOption(q)">+ Add Option</b-button>
                </template>

                <!-- Boolean options -->
                <template v-if="q.type === 'boolean'">
                  <b-row>
                    <b-col md="6">
                      <b-form-group label="Yes label" class="mb-2">
                        <b-form-input v-model="q.extra.yes_label" placeholder="Yes" size="sm" />
                      </b-form-group>
                    </b-col>
                    <b-col md="6">
                      <b-form-group label="No label" class="mb-2">
                        <b-form-input v-model="q.extra.no_label" placeholder="No" size="sm" />
                      </b-form-group>
                    </b-col>
                  </b-row>
                </template>
              </div>
              <b-button size="sm" variant="outline-danger" class="ml-2" @click="removeQuestion(index)">
                <i class="material-icons" style="font-size: 16px;">delete</i>
              </b-button>
            </div>
          </b-card>
        </draggable>

        <hr />

        <div class="d-flex justify-content-between">
          <div>
            <span v-if="isEdit && surveyToken" class="text-muted small">
              Survey link: <code>{{ surveyUrl }}</code>
              <b-button size="sm" variant="link" @click="copySurveyLink" class="p-0 ml-1">Copy</b-button>
            </span>
          </div>
          <b-button type="submit" variant="primary" :disabled="isSaving">
            {{ isSaving ? 'Saving...' : (isEdit ? 'Update Survey' : 'Create Survey') }}
          </b-button>
        </div>
      </b-form>
    </b-card>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { get } from 'lodash';
import { QUESTION_TYPES, SURVEY_STATUSES } from '../../common/constants';
import PageHeader from '../../components/Ui/PageHeader.vue';
import draggable from 'vuedraggable';

export default {
  name: 'SurveyEditor',
  components: { PageHeader, draggable },
  data() {
    return {
      isEdit: false,
      isSaving: false,
      surveyToken: null,
      form: {
        title: '',
        description: '',
        status: 'draft',
        collect_email: true,
        collect_name: true,
        collect_phone: false,
        welcome_message: '',
        thank_you_message: 'Thank you for completing this survey!',
        questions: [],
      },
    };
  },
  computed: {
    ...mapGetters('auth', { user: 'getLoggedInUser' }),
    questionTypeOptions() {
      return QUESTION_TYPES;
    },
    statusOptions() {
      return SURVEY_STATUSES;
    },
    surveyUrl() {
      const signUrl = process.env.VUE_APP_SIGN_URL || 'http://localhost:8084';
      return `${signUrl}/survey/${this.surveyToken}`;
    },
    schoolLogo() {
      return get(this.user, 'school.logo_url') || get(this.user, 'school.small_logo_url') || null;
    },
    schoolName() {
      return get(this.user, 'school.name') || '';
    },
  },
  methods: {
    ...mapActions('survey', ['getSurvey', 'createSurvey', 'updateSurvey']),
    addQuestion() {
      this.form.questions.push({
        title: '',
        type: 'short_text',
        extra: {},
        placeholder: '',
        order_num: this.form.questions.length,
        is_required: false,
      });
    },
    removeQuestion(index) {
      this.form.questions.splice(index, 1);
      this.reorderQuestions();
    },
    reorderQuestions() {
      this.form.questions.forEach((q, i) => { q.order_num = i; });
    },
    onTypeChange(q) {
      q.extra = {};
      q.placeholder = '';
      if (q.type === 'single_select' || q.type === 'multi_select') {
        q.extra = { options: [''] };
      }
      if (q.type === 'boolean') {
        q.extra = { yes_label: 'Yes', no_label: 'No' };
      }
    },
    addOption(q) {
      if (!q.extra.options) q.extra.options = [];
      q.extra.options.push('');
    },
    removeOption(q, index) {
      q.extra.options.splice(index, 1);
    },
    copySurveyLink() {
      navigator.clipboard.writeText(this.surveyUrl).then(() => {
        this.makeToast('success', 'Survey link copied');
      });
    },
    async handleSave() {
      this.isSaving = true;
      try {
        const user = this.$store.getters['auth/getLoggedInUser'];
        const payload = { ...this.form, school: user.school.id };

        if (this.isEdit) {
          await this.updateSurvey({ id: this.$route.params.id, data: payload });
          this.makeToast('success', 'Survey updated');
        } else {
          await this.createSurvey(payload);
          this.makeToast('success', 'Survey created');
          this.$router.push({ name: 'surveys-list' });
        }
      } catch (err) {
        this.makeToast('danger', 'Failed to save survey');
      } finally {
        this.isSaving = false;
      }
    },
    async loadSurvey() {
      try {
        const res = await this.getSurvey(this.$route.params.id);
        const data = res.data;
        this.surveyToken = data.token;
        this.form = {
          title: data.title,
          description: data.description || '',
          status: data.status,
          collect_email: data.collect_email,
          collect_name: data.collect_name,
          collect_phone: data.collect_phone,
          welcome_message: data.welcome_message || '',
          thank_you_message: data.thank_you_message || '',
          questions: (data.questions || []).map(q => ({
            title: q.title,
            type: q.type,
            extra: q.extra || {},
            placeholder: q.placeholder || '',
            order_num: q.order_num,
            is_required: q.is_required,
          })),
        };
      } catch (err) {
        this.makeToast('danger', 'Failed to load survey');
      }
    },
  },
  async created() {
    this.isEdit = !!this.$route.params.id;
    if (this.isEdit) {
      await this.loadSurvey();
    }
  },
};
</script>

<style scoped>
.survey-brand-preview {
  display: flex;
  align-items: center;
  background: linear-gradient(135deg, #fff, #f8f9fa);
  border-radius: 8px;
  padding: 14px 18px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  border-left: 3px solid var(--primary-color, #3B86F5);
}
.survey-brand-preview-logo {
  height: 48px;
  max-width: 140px;
  object-fit: contain;
}
.survey-brand-preview-title {
  font-weight: 600;
  font-size: 1rem;
  color: var(--dark-color, #181818);
}
</style>
